import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { HelpIcon } from './HelpIcon';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import { 
  CogIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  MagnifyingGlassIcon,
  LinkIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface RatingSettings {
  id: string;
  mode: 'standard' | 'strict';
  google_place_id?: string;
  google_place_name?: string;
  yelp_business_id?: string;
  facebook_page_id?: string;
  custom_feedback_email?: string;
  policy_safe_language?: string;
  thank_you_message?: string;
}

interface GooglePlace {
  place_id: string;
  name: string;
  formatted_address: string;
  rating?: number;
  user_ratings_total?: number;
  business_status?: string;
  types: string[];
}

interface RatingSettingsProps {
  tenantId?: string; // Made optional - will get from auth context if not provided
}

export const RatingSettings: React.FC<RatingSettingsProps> = ({ tenantId: propTenantId }) => {
  const { tenant } = useAuth();
  const tenantId = propTenantId || tenant?.id || '';
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showPlaceSearch, setShowPlaceSearch] = useState(false);
  const [pendingPlace, setPendingPlace] = useState<GooglePlace | null>(null);
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  // Fetch current settings
  const { data: settings, isLoading } = useQuery<RatingSettings>({
    queryKey: ['rating-settings', tenantId],
    queryFn: async () => {
      const response = await api.ratingGate.getSettings();
      return response.data;
    },
    enabled: !!tenantId // Only fetch if tenantId is available
  });

  // Fetch current verification status to check if we need to warn about reset
  const { data: currentVerification } = useQuery({
    queryKey: ['verification-status'],
    queryFn: async () => {
      const response = await api.analytics.getVerificationStatus();
      return response.data;
    },
    enabled: !!tenantId,
  });

  // Search Google Places
  const { data: places, isLoading: searchingPlaces } = useQuery<{places: GooglePlace[], total_results: number}>({
    queryKey: ['google-places-search', searchQuery],
    queryFn: async () => {
      const response = await api.ratingGate.searchPlaces(searchQuery);
      return response.data;
    },
    enabled: !!searchQuery && showPlaceSearch
  });

  // Fetch public settings to check if rating gate mode is locked
  const { data: publicSettings = [] } = useQuery<Array<{ key: string; value: string; description?: string }>>({
    queryKey: ['public-settings'],
    queryFn: async () => {
      try {
        const response = await api.serverAdmin.getPublicSettings();
        return response.data || [];
      } catch (error) {
        // If user doesn't have access, return empty array
        console.warn('Failed to load public settings:', error);
        return [];
      }
    }
  });

  // Check if rating gate mode is locked
  const ratingGateModeLocked = React.useMemo(() => {
    const lockSetting = publicSettings.find(s => s.key === 'rating_gate_mode_lock');
    return lockSetting?.value?.toLowerCase() === 'true';
  }, [publicSettings]);

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsData: Partial<RatingSettings>) => {
      const response = await api.ratingGate.updateSettings(settingsData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rating-settings', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['verification-status'] }); // Invalidate verification status
      // Only invalidate dashboard-kpis if google_place_id changed (to update business name)
      // Don't invalidate analytics-overview as it's not affected by place_id change
      queryClient.invalidateQueries({ queryKey: ['analytics', 'dashboard', 'kpis'] });
    }
  });

  const [formData, setFormData] = useState<Partial<RatingSettings>>({});

  React.useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleInputChange = (field: keyof RatingSettings, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    updateSettingsMutation.mutate(formData);
  };

  const handlePlaceSelect = async (place: GooglePlace) => {
    // Check if there's an existing business with verification that would be reset
    const currentPlaceId = settings?.google_place_id;
    const verificationStatus = currentVerification?.status;
    
    // Show warning if:
    // 1. There's an existing linked business
    // 2. There's a verification status (pending or verified)
    // 3. The new place_id is different from the current one
    if (currentPlaceId && verificationStatus && 
        place.place_id !== currentPlaceId && 
        (verificationStatus === 'pending' || verificationStatus === 'verified')) {
      setPendingPlace(place);
      setShowWarningDialog(true);
      return;
    }
    
    // Proceed with linking
    proceedWithPlaceSelection(place);
  };

  const proceedWithPlaceSelection = async (place: GooglePlace) => {
    const updatedFormData = {
      ...formData,
      google_place_id: place.place_id,
      google_place_name: place.name
    };
    setFormData(updatedFormData);
    setShowPlaceSearch(false);
    setSearchQuery('');
    setPendingPlace(null);
    setShowWarningDialog(false);
    
    // Auto-save when place is selected
    try {
      await updateSettingsMutation.mutateAsync(updatedFormData);
    } catch (error) {
      // Error is handled by the mutation
      console.error('Failed to save place selection:', error);
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingPlace) {
      proceedWithPlaceSelection(pendingPlace);
    }
  };

  const handleCancelSwitch = () => {
    setPendingPlace(null);
    setShowWarningDialog(false);
  };

  const renderModeSettings = () => (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-2 ${ratingGateModeLocked ? 'text-gray-400' : 'text-gray-700'}`}>
          Rating Gate Mode
          {ratingGateModeLocked && (
            <span className="ml-2 text-xs text-gray-500">(Locked by administrator)</span>
          )}
        </label>
        <div className={`space-y-2 ${ratingGateModeLocked ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <label className={`flex items-center ${ratingGateModeLocked ? 'cursor-not-allowed' : ''}`}>
            <input
              type="radio"
              name="mode"
              value="standard"
              checked={formData.mode === 'standard'}
              onChange={(e) => handleInputChange('mode', e.target.value)}
              disabled={ratingGateModeLocked}
              className="mr-2"
            />
            <div>
              <div className="font-medium">Standard Mode</div>
              <div className="text-sm text-gray-500">
                Allows Yelp solicitation and standard review requests
              </div>
            </div>
          </label>
          <label className={`flex items-center ${ratingGateModeLocked ? 'cursor-not-allowed' : ''}`}>
            <input
              type="radio"
              name="mode"
              value="strict"
              checked={formData.mode === 'strict'}
              onChange={(e) => handleInputChange('mode', e.target.value)}
              disabled={ratingGateModeLocked}
              className="mr-2"
            />
            <div>
              <div className="font-medium">Strict Mode</div>
              <div className="text-sm text-gray-500">
                Maximum policy compliance - no Yelp solicitation, neutral language only
              </div>
            </div>
          </label>
        </div>
      </div>

      {formData.mode === 'strict' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Strict Mode Active
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Yelp solicitation is disabled</li>
                  <li>Only neutral language will be used</li>
                  <li>Focus on experience sharing rather than review platforms</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderGooglePlacesSettings = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Google Business Listing
        </label>
        
        {formData.google_place_name ? (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-3">
            <div>
              <div className="font-medium text-green-800">{formData.google_place_name}</div>
              <div className="text-sm text-green-600">Google Place ID: {formData.google_place_id}</div>
            </div>
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, google_place_id: undefined, google_place_name: undefined }));
                setShowPlaceSearch(true);
              }}
              className="text-green-600 hover:text-green-800"
            >
              Change
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowPlaceSearch(true)}
            className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-md p-3 hover:bg-blue-100"
          >
            <MagnifyingGlassIcon className="h-5 w-5 text-blue-600" />
            <span className="text-blue-700">Search for your Google Business listing</span>
          </button>
        )}
      </div>

      {showPlaceSearch && (
        <div className="border border-gray-200 rounded-md p-4">
          <div className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Search for your business..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {searchingPlaces && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            )}

            {places && places.places.length > 0 && (
              <>
                <div className="text-sm text-gray-600 font-medium">
                  Found {places.places.length} result{places.places.length !== 1 ? 's' : ''} - Select your business:
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {places.places.map((place) => (
                    <button
                      key={place.place_id}
                      onClick={() => handlePlaceSelect(place)}
                      className="w-full text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-blue-300 transition-colors"
                    >
                      <div className="font-medium">{place.name}</div>
                      <div className="text-sm text-gray-600">{place.formatted_address}</div>
                      {place.rating && (
                        <div className="text-sm text-gray-500">
                          ⭐ {place.rating} ({place.user_ratings_total} reviews)
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}

            {places && places.places.length === 0 && !searchingPlaces && (
              <div className="text-sm text-gray-500 py-4 text-center">
                No results found. Try searching with a different business name or address.
              </div>
            )}

            <button
              onClick={() => setShowPlaceSearch(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderOtherPlatformsSettings = () => (
    <div className="space-y-4">
      <div>
        <label htmlFor="yelp_business_id" className="block text-sm font-medium text-gray-700 mb-2">
          Yelp Business ID (Optional)
        </label>
        <input
          type="text"
          id="yelp_business_id"
          value={formData.yelp_business_id || ''}
          onChange={(e) => handleInputChange('yelp_business_id', e.target.value)}
          placeholder="e.g., your-business-name-san-francisco"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          disabled={formData.mode === 'strict'}
        />
        {formData.mode === 'strict' && (
          <p className="text-sm text-yellow-600 mt-1">
            Yelp integration is disabled in strict mode
          </p>
        )}
      </div>

      <div>
        <label htmlFor="facebook_page_id" className="block text-sm font-medium text-gray-700 mb-2">
          Facebook Page ID (Optional)
        </label>
        <input
          type="text"
          id="facebook_page_id"
          value={formData.facebook_page_id || ''}
          onChange={(e) => handleInputChange('facebook_page_id', e.target.value)}
          placeholder="e.g., YourBusinessPage"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label htmlFor="custom_feedback_email" className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          Custom Feedback Email (Optional)
          {customFeedbackEmailHelp && (
            <HelpIcon
              tooltip="Learn about custom feedback email"
              modalTitle={customFeedbackEmailHelp.title}
              modalContent={customFeedbackEmailHelp.content}
              size="sm"
              variant="subtle"
            />
          )}
        </label>
        <input
          type="email"
          id="custom_feedback_email"
          value={formData.custom_feedback_email || ''}
          onChange={(e) => handleInputChange('custom_feedback_email', e.target.value)}
          placeholder="feedback@yourbusiness.com"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-sm text-gray-500 mt-1">
          Email address for private feedback submissions. Customers with 1-3 star ratings will see a button to email you directly.
        </p>
      </div>
    </div>
  );

  const renderCustomMessages = () => (
    <div className="space-y-4">
      <div>
        <label htmlFor="policy_safe_language" className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          Policy-Safe Language
          {policyLanguageHelp && (
            <HelpIcon
              tooltip="Learn about policy-safe language"
              modalTitle={policyLanguageHelp.title}
              modalContent={policyLanguageHelp.content}
              size="sm"
              variant="subtle"
            />
          )}
        </label>
        <textarea
          id="policy_safe_language"
          value={formData.policy_safe_language || ''}
          onChange={(e) => handleInputChange('policy_safe_language', e.target.value)}
          rows={3}
          placeholder="We value your feedback and would appreciate you sharing your experience to help other customers make informed decisions."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-sm text-gray-500 mt-1">
          Custom message shown on the rating page
        </p>
      </div>

      <div>
        <label htmlFor="thank_you_message" className="block text-sm font-medium text-gray-700 mb-2">
          Thank You Message
        </label>
        <textarea
          id="thank_you_message"
          value={formData.thank_you_message || ''}
          onChange={(e) => handleInputChange('thank_you_message', e.target.value)}
          rows={2}
          placeholder="Thank you for taking the time to share your experience with us."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-sm text-gray-500 mt-1">
          Message shown after rating submission
        </p>
      </div>
    </div>
  );

  if (!tenantId) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-center">
          <p className="text-gray-600">Loading tenant information...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const ratingGateHelp = getHelpContent('ratingGateFlow');
  const policyLanguageHelp = getHelpContent('ratingGatePolicyLanguage');
  const customFeedbackEmailHelp = getHelpContent('ratingGateCustomFeedbackEmail');

  return (
    <TooltipProvider>
      {/* Warning Dialog */}
      {showWarningDialog && pendingPlace && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCancelSwitch} />
            
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                      Reset Verification?
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are switching to a different business. This will reset your verification status 
                        {currentVerification?.status === 'verified' ? ' (currently verified)' : ' (currently pending)'}.
                        You'll need to verify the new business after linking.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  onClick={handleConfirmSwitch}
                >
                  Continue & Reset
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={handleCancelSwitch}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <CogIcon className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Rating Gate Settings</h2>
            {ratingGateHelp && (
              <HelpIcon
                tooltip="Learn about rating gate settings and configuration"
                modalTitle={ratingGateHelp.title}
                modalContent={ratingGateHelp.content}
                size="sm"
                variant="subtle"
              />
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Configure how customers are directed to leave reviews based on their rating
          </p>
        </div>

      <div className="p-6 space-y-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Be sure to save your settings
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Click the "Save Settings" button below to apply your changes. Changes will not be saved until you click the save button.
              </p>
            </div>
          </div>
        </div>

        {renderModeSettings()}
        {renderGooglePlacesSettings()}
        {renderOtherPlatformsSettings()}
        {renderCustomMessages()}

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={updateSettingsMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {updateSettingsMutation.isSuccess && (
          <div className="mt-4 flex items-center space-x-2 text-green-600 bg-green-50 border border-green-200 rounded-md p-3">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="font-medium">Settings saved successfully! The dashboard will update shortly.</span>
          </div>
        )}

        {updateSettingsMutation.isError && (
          <div className="mt-4 flex items-center space-x-2 text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            <XCircleIcon className="h-5 w-5" />
            <span className="font-medium">Failed to save settings. Please try again.</span>
          </div>
        )}
      </div>
      </div>
    </TooltipProvider>
  );
};
